import { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { Package, Plus, Edit2, Trash2, Tag, X, Check, ChevronsUpDown, Eye, LayoutGrid, List, UploadCloud, Loader2, ArrowUp, ArrowDown, Archive, ArchiveRestore, CheckCircle2, XCircle, ExternalLink, Copy, RotateCcw, FileText, Layers, GitBranch, CreditCard, RefreshCw, Sparkles, Star } from "lucide-react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useListBundles, useCreateBundle, useUpdateBundle, useDeleteBundle, useReorderBundles, getListBundlesQueryKey, useListCptCodes, useListMasterCptCodes, useListProviders, useGetProvider, useGetLocalities, useListBundleCategories, useGetCptCategories, useGetCptSubcategories, API_BASE } from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { navigatePostSaveBundle } from "@/lib/utils";

interface BundleWithCpts {
  id: string;
  name: string;
  description: string | null;
  category: string;
  cptCodeIds: number[];
  totalPrice: number;
  discountPercent: number;
  finalPrice: number;
  status: string;
  version: number;
  type?: string;
  createdAt: string;
  thumbnailUrl?: string | null;
  tags?: string[];
  isSubscription?: boolean;
  subscriptionInterval?: string | null;
  recurringAmount?: number | null;
  billingCycles?: number | null;
  isFeatured?: boolean;
  _count?: { appointments: number };
  cptCodes?: { id: string; code: string; description: string; price: number }[];
  participatingProviders?: { id: string; user?: { firstName: string; lastName: string } }[];
}

export default function Bundles() {
  const [, setLocation] = useLocation();
  const [editId, setEditId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [viewBundle, setViewBundle] = useState<BundleWithCpts | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState("");
  const [archiveConfirmBundle, setArchiveConfirmBundle] = useState<BundleWithCpts | null>(null);
  const [form, setForm] = useState({ name: "", description: "", category: "Medical", categoryType: "Office Visits", discountPercent: "0", finalPrice: "", status: "active" });
  const [selectedCpts, setSelectedCpts] = useState<string[]>([]);
  const [cptUnits, setCptUnits] = useState<Record<string, number>>({});
  const [cptAdjustments, setCptAdjustments] = useState<Record<string, { type: "percent" | "fixed"; value: number; percent?: number; fixed?: number; adjustedPrice?: number }>>({});
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Custom Items State
  const [customItems, setCustomItems] = useState<{ id: string; name: string; description?: string; price: number; unit?: number }[]>([]);
  const [customName, setCustomName] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [customUnit, setCustomUnit] = useState("1");

  // Thumbnail Image State
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Tag Cloud State
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);

  // Subscription / Payment Plan State
  const [isSubscription, setIsSubscription] = useState<boolean>(false);
  const [subscriptionInterval, setSubscriptionInterval] = useState<string>("MONTHLY");
  const [recurringAmount, setRecurringAmount] = useState<string>("");
  const [billingCycles, setBillingCycles] = useState<string>("");

  const fetchExistingTags = async () => {
    try {
      let token = localStorage.getItem("token") || "";
      if (!token) {
        try {
          const u = JSON.parse(localStorage.getItem("user") || "{}");
          if (u.token) token = u.token;
        } catch (err) { }
      }
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/bundles/tags`, { headers });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setExistingTags(json.data);
      }
    } catch (err) {
      console.error("Failed to fetch bundle tags:", err);
    }
  };

  useEffect(() => {
    fetchExistingTags();
  }, [open]);

  // Popup CPT Filters State
  const [popupCodeType, setPopupCodeType] = useState<string>("all");
  const [popupCategory, setPopupCategory] = useState<string>("all");
  const [popupSubCategory, setPopupSubCategory] = useState<string>("all");

  const getImageUrl = (url?: string | null) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const baseUrl = API_BASE.replace(/\/v1.*$/, "");
    return `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("thumbnail", file);
      let token = localStorage.getItem("token") || "";
      if (!token) {
        try {
          const u = JSON.parse(localStorage.getItem("user") || "{}");
          if (u.token) token = u.token;
        } catch (err) { }
      }
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/upload/thumbnail`, {
        method: "POST",
        headers,
        body: formData,
      });
      const json = await res.json();
      if (json.url) {
        setThumbnailUrl(json.url);
      } else {
        throw new Error(json.error || "Upload failed");
      }
    } catch (err: any) {
      toast({ title: "Upload Failed", description: err.message || "Failed to upload thumbnail", variant: "destructive" });
    } finally {
      setUploadingImage(false);
    }
  };

  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch (e) { }
    }
  }, []);

  const isGroup = localStorage.getItem("practiceType") === "group";
  const [bundleScope, setBundleScope] = useState<"GROUP" | "PROVIDER">("GROUP");
  const [participatingProviderIds, setParticipatingProviderIds] = useState<string[]>([]);
  const [cptSearch, setCptSearch] = useState("");
  const [cptOpen, setCptOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<"active" | "inactive" | "archived" | "deleted">("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedCodeType, setSelectedCodeType] = useState<string>("all");
  const { data: bundlesData, isLoading } = useListBundles({
    page: currentPage,
    limit: itemsPerPage,
    status: activeTab
  });
  const bundles = bundlesData?.items || [];
  const totalEntries = bundlesData?.totalCount || 0;
  const activeCount = (bundlesData as any)?.activeCount || 0;
  const inactiveCount = (bundlesData as any)?.inactiveCount || 0;
  const archivedCount = (bundlesData as any)?.archivedCount || 0;
  const deletedCount = (bundlesData as any)?.deletedCount || 0;
  const totalPages = bundlesData?.totalPages || 1;

  const { data: providerCptCodesData } = useListCptCodes({ limit: 5000 });
  const providerCptCodes = providerCptCodesData?.items || [];
  const { data: rawMasterCodesData } = useListMasterCptCodes({ limit: 5000 });
  const rawMasterCodes = rawMasterCodesData?.items || [];
  const { data: localitiesData = [] } = useGetLocalities();

  const { data: currentProviderData } = useGetProvider(user?.id || "", { query: { enabled: !!user } });
  const myLinkedProviders = currentProviderData?.linkedProviders || [];


  const { data: rawBundleCategoriesData = [] } = useListBundleCategories();
  const { data: cptCategoriesData = [] } = useGetCptCategories();
  const { data: cptSubcategoriesData = [] } = useGetCptSubcategories();



  const createBundle = useCreateBundle();
  const updateBundle = useUpdateBundle();
  const deleteBundle = useDeleteBundle();
  const reorderBundles = useReorderBundles();

  const [sortBy, setSortBy] = useState<string>("custom");

  const filteredByTagBundles = useMemo(() => {
    let list = [...(bundles as unknown as BundleWithCpts[])];
    if (selectedTagFilter) {
      list = list.filter((b) => Array.isArray(b.tags) && b.tags.includes(selectedTagFilter));
    }
    return list;
  }, [bundles, selectedTagFilter]);

  const sortedBundles = useMemo(() => {
    const list = [...filteredByTagBundles];
    if (sortBy === "name_asc") {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "name_desc") {
      list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    } else if (sortBy === "price_asc") {
      list.sort((a, b) => (a.finalPrice || a.totalPrice || 0) - (b.finalPrice || b.totalPrice || 0));
    } else if (sortBy === "price_desc") {
      list.sort((a, b) => (b.finalPrice || b.totalPrice || 0) - (a.finalPrice || a.totalPrice || 0));
    }
    return list;
  }, [filteredByTagBundles, sortBy]);

  const handleMove = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sortedBundles.length) return;

    const newList = [...sortedBundles];
    const temp = newList[index];
    newList[index] = newList[targetIndex];
    newList[targetIndex] = temp;

    const reorderPayload = newList.map((item, idx) => ({
      id: item.id,
      sortOrder: idx
    }));

    reorderBundles.mutate(reorderPayload, {
      onSuccess: () => {
        toast({ title: "Sequence updated", description: "Bundle display order saved." });
      },
      onError: (err: any) => {
        toast({ title: "Reorder failed", description: err.message, variant: "destructive" });
      }
    });
  };

  const categoryOptions = useMemo(() => {
    const base = [
      "Primary Care",
      "Cardiology",
      "Diagnostics",
      "Surgical",
      "Wellness",
      "Orthopedics",
      "Pediatrics",
      "Dental",
      "Mental Health",
      "General",
      "Preventive",
      "Cardiac",
      "Orthopedic",
      "Other"
    ];
    if (form.category && !base.includes(form.category)) {
      return [form.category, ...base];
    }
    return base;
  }, [form.category]);

  const effectiveMedicareRate = useMemo(() => {
    if (isGroup && bundleScope === 'PROVIDER' && participatingProviderIds.length > 0) {
      const selectedProviderId = String(participatingProviderIds[0]);
      const provider = myLinkedProviders.find((p: any) => String(p.providerId || p.id) === selectedProviderId);
      if (provider && provider.medicareCharge) {
        return parseFloat(String(provider.medicareCharge).replace('%', '')) || 100;
      }
    }
    return parseFloat(user?.provider?.medicareCharge || "100") || 100;
  }, [isGroup, bundleScope, participatingProviderIds, myLinkedProviders, user]);

  const [selectedLocalityId, setSelectedLocalityId] = useState<string>("all");

  const normalizeStateCode = (st: string) => {
    if (!st) return "";
    const s = st.trim().toUpperCase();
    if (s === "NEW YORK" || s === "NEW YORK STATE" || s === "N.Y." || s === "NY") return "NY";
    if (s === "CALIFORNIA" || s === "CA") return "CA";
    if (s === "TEXAS" || s === "TX") return "TX";
    if (s === "FLORIDA" || s === "FL") return "FL";
    return s;
  };

  const effectiveUser = currentProviderData || user;

  const providerLocalities = useMemo(() => {
    const selectedLocalityKeys = new Set<string>();

    const addrList = effectiveUser?.addresses || effectiveUser?.user?.addresses;
    if (addrList && Array.isArray(addrList)) {
      addrList.forEach((a: any) => {
        const ids = a.localityIds || (a.localityId ? [a.localityId] : []);
        if (Array.isArray(ids)) {
          ids.forEach((id: any) => {
            if (id) selectedLocalityKeys.add(String(id).trim());
          });
        }
      });
    }

    const locList = effectiveUser?.localities || effectiveUser?.provider?.localities || effectiveUser?.user?.localities;
    if (selectedLocalityKeys.size === 0 && locList && Array.isArray(locList)) {
      locList.forEach((l: any) => {
        if (typeof l === "string") {
          selectedLocalityKeys.add(l.trim());
        } else if (l && typeof l === "object") {
          if (l.id) selectedLocalityKeys.add(String(l.id).trim());
          if (l.localityNumber) selectedLocalityKeys.add(String(l.localityNumber).trim());
          if (l.name) selectedLocalityKeys.add(String(l.name).trim());
        }
      });
    }

    const locMap = new Map<string, any>();
    const targetKeys = Array.from(selectedLocalityKeys);

    if (Array.isArray(localitiesData) && localitiesData.length > 0) {
      if (targetKeys.length > 0) {
        localitiesData.forEach((loc: any) => {
          const locIdStr = String(loc.id).trim();
          const locNumStr = String(loc.localityNumber || '').trim();
          const locNameLower = String(loc.name || '').trim().toLowerCase();

          for (const key of targetKeys) {
            if (!key) continue;
            const keyLower = key.toLowerCase();
            if (locIdStr === key || (locNumStr && locNumStr === key) || locNameLower === keyLower) {
              locMap.set(locIdStr, loc);
              break;
            }
          }
        });
      }

      // Fallback: If no specific practice localities are selected yet, filter by address state
      if (locMap.size === 0) {
        const stateKeys = new Set<string>();
        if (addrList && Array.isArray(addrList)) {
          addrList.forEach((a: any) => {
            if (a.state) {
              const norm = normalizeStateCode(a.state);
              if (norm) stateKeys.add(norm);
            }
          });
        }
        if (stateKeys.size === 0) stateKeys.add("NY");

        localitiesData.forEach((loc: any) => {
          if (loc.state && stateKeys.has(normalizeStateCode(loc.state))) {
            locMap.set(String(loc.id).trim(), loc);
          }
        });
      }
    }

    return Array.from(locMap.values());
  }, [effectiveUser, localitiesData]);

  const providerLocations = useMemo(() => {
    const addrList = effectiveUser?.addresses || effectiveUser?.user?.addresses || [];
    const locations: Array<{
      id: string;
      displayLabel: string;
      locality: any;
      localityId?: string;
    }> = [];

    if (Array.isArray(addrList) && addrList.length > 0) {
      addrList.forEach((addr: any, index: number) => {
        const street = [addr.buildingNo, addr.street1, addr.street2].filter(Boolean).join(" ");
        const cityStateZip = [addr.city, addr.state, addr.zipCode].filter(Boolean).join(", ");
        const fullAddress = [street, cityStateZip].filter(Boolean).join(" - ") || `Location ${index + 1}`;
        const isPrimaryTag = addr.isPrimary ? " (Primary)" : "";
        const displayLabel = `${fullAddress}${isPrimaryTag}`;

        let targetLocality: any = null;
        const locIds = addr.localityIds || (addr.localityId ? [addr.localityId] : []);

        if (Array.isArray(localitiesData) && localitiesData.length > 0) {
          if (locIds.length > 0) {
            targetLocality = localitiesData.find((l: any) =>
              locIds.some((id: any) => String(l.id) === String(id) || String(l.localityNumber) === String(id))
            );
          }
          if (!targetLocality && addr.state) {
            const normState = normalizeStateCode(addr.state);
            targetLocality = localitiesData.find((l: any) => normalizeStateCode(l.state) === normState);
          }
        }

        locations.push({
          id: addr.id || `location-${index}`,
          displayLabel,
          locality: targetLocality || providerLocalities[0] || null,
          localityId: targetLocality?.id || providerLocalities[0]?.id
        });
      });
    }

    if (locations.length === 0 && providerLocalities.length > 0) {
      providerLocalities.forEach((loc: any) => {
        locations.push({
          id: String(loc.id),
          displayLabel: `${loc.name}${loc.state ? `, ${loc.state}` : ''}`,
          locality: loc,
          localityId: String(loc.id)
        });
      });
    }

    return locations;
  }, [effectiveUser, localitiesData, providerLocalities]);

  const [selectedLocationId, setSelectedLocationId] = useState<string>("all");

  useEffect(() => {
    if (providerLocations.length > 0) {
      const isAlreadySelectedValid = providerLocations.some(l => String(l.id) === String(selectedLocationId));
      if (!selectedLocationId || selectedLocationId === "all" || !isAlreadySelectedValid) {
        setSelectedLocationId(String(providerLocations[0].id));
      }
    }
  }, [providerLocations, selectedLocationId]);

  const selectedLocation = useMemo(() => {
    return providerLocations.find(l => String(l.id) === String(selectedLocationId)) || providerLocations[0] || null;
  }, [selectedLocationId, providerLocations]);

  const selectedLocality = selectedLocation?.locality || providerLocalities[0] || null;

  const getRateForSelectedLocality = (ratesObj: any, selectedLocObj: any, loc: any, codeTypeName: string, codeTypeId: string) => {
    if (!ratesObj || typeof ratesObj !== "object") return null;

    const getValueFromSubObj = (subObj: any): number | null => {
      if (!subObj || typeof subObj !== "object") {
        if (typeof subObj === "number") return subObj;
        if (typeof subObj === "string" && !isNaN(Number(subObj))) return Number(subObj);
        return null;
      }
      if (codeTypeName && subObj[codeTypeName] !== undefined && subObj[codeTypeName] !== null && !isNaN(Number(subObj[codeTypeName]))) {
        return Number(subObj[codeTypeName]);
      }
      if (codeTypeId && subObj[codeTypeId] !== undefined && subObj[codeTypeId] !== null && !isNaN(Number(subObj[codeTypeId]))) {
        return Number(subObj[codeTypeId]);
      }

      const keys = Object.keys(subObj);
      if (codeTypeName) {
        const matchedKey = keys.find(k => k.toUpperCase() === codeTypeName.toUpperCase());
        if (matchedKey && subObj[matchedKey] !== undefined && subObj[matchedKey] !== null && !isNaN(Number(subObj[matchedKey]))) {
          return Number(subObj[matchedKey]);
        }
      }
      if (subObj.default !== undefined && subObj.default !== null && !isNaN(Number(subObj.default))) {
        return Number(subObj.default);
      }
      if (subObj.General !== undefined && subObj.General !== null && !isNaN(Number(subObj.General))) {
        return Number(subObj.General);
      }

      for (const k of keys) {
        const val = Number(subObj[k]);
        if (!isNaN(val) && typeof subObj[k] !== "object" && subObj[k] !== null && subObj[k] !== undefined) return val;
      }
      return null;
    };

    const possibleKeys: string[] = [];

    if (selectedLocObj) {
      if (selectedLocObj.id) possibleKeys.push(String(selectedLocObj.id).trim());
      if (selectedLocObj.raw?.id) possibleKeys.push(String(selectedLocObj.raw.id).trim());

      const locIdx = providerLocations.findIndex(l => String(l.id) === String(selectedLocObj.id));
      if (locIdx !== -1) {
        possibleKeys.push(`loc_${locIdx}`);
        possibleKeys.push(`location-${locIdx}`);
      }

      if (selectedLocObj.displayLabel) {
        const addrPart = selectedLocObj.displayLabel.split(" - ")[0];
        if (addrPart) possibleKeys.push(String(addrPart).trim());
      }
      if (selectedLocObj.raw?.address1) possibleKeys.push(String(selectedLocObj.raw.address1).trim());
      if (selectedLocObj.raw?.street1) possibleKeys.push(String(selectedLocObj.raw.street1).trim());
    }

    if (loc) {
      if (loc.id) possibleKeys.push(String(loc.id).trim());
      if (loc.localityNumber) possibleKeys.push(String(loc.localityNumber).trim());
      if (loc.name && loc.state) possibleKeys.push(`${loc.name} (${loc.state}${loc.city ? ' - ' + loc.city : ''})`);
      if (loc.name && loc.state) possibleKeys.push(`${loc.name} (${loc.state})`);
      if (loc.name) possibleKeys.push(String(loc.name).trim());
    }

    // STEP 1: Check if a location-specific percentage exists for this location
    for (const key of possibleKeys) {
      if (!key) continue;
      const targetRate = ratesObj[key];
      const val = getValueFromSubObj(targetRate);
      if (val !== null) return val;
    }

    for (const key of Object.keys(ratesObj)) {
      const targetRate = ratesObj[key];
      if (!targetRate || typeof targetRate !== "object") continue;

      const keyLower = key.toLowerCase();
      const matchFound = possibleKeys.some(k => k.toLowerCase() === keyLower || keyLower.includes(k.toLowerCase()) || k.toLowerCase().includes(keyLower));

      if (matchFound) {
        const val = getValueFromSubObj(targetRate);
        if (val !== null) return val;
      }
    }

    // STEP 2: Fallback to global default percentage rate in ratesObj
    const globalVal = getValueFromSubObj(ratesObj);
    if (globalVal !== null) return globalVal;

    return null;
  };

  const cptCodes = useMemo(() => {
    const pwGpci = selectedLocality?.pwGpci ?? 1.0;
    const peGpci = selectedLocality?.peGpci ?? 1.0;
    const mpGpci = selectedLocality?.mpGpci ?? 1.0;

    const masterMap = new Map<string, any>();
    rawMasterCodes.forEach((m: any) => masterMap.set(m.code, m));

    const allCodes: any[] = [];
    const processedCodes = new Set<string>();

    const codesSource = providerCptCodes || [];

    codesSource.forEach((item: any) => {
      const master = masterMap.get(item.code) || item;
      const codeTypeObj = item.codeType || master?.codeType;
      const codeTypeName = (typeof codeTypeObj === "string" ? codeTypeObj : codeTypeObj?.name) || "PFS";
      const isCLFS = codeTypeName.toUpperCase() === "CLFS";

      const workRvu = master.workRvu ?? 0.0;
      const peRvu = master.peRvu ?? 0.0;
      const mpRvu = master.mpRvu ?? 0.0;
      const cf = master.conversionFactor || 33.29;

      let baseStandardPrice = master.standardPrice || 0;
      if (!isCLFS && (workRvu > 0 || peRvu > 0 || mpRvu > 0)) {
        const rvuGpciSum = (workRvu * pwGpci) + (peRvu * peGpci) + (mpRvu * mpGpci);
        if (rvuGpciSum > 0) {
          baseStandardPrice = rvuGpciSum * cf;
        }
      }

      let codeTypePct = effectiveMedicareRate || 100;
      const ratesObj = effectiveUser?.provider?.categoryRates || effectiveUser?.categoryRates || user?.provider?.categoryRates || user?.categoryRates;
      if (ratesObj && typeof ratesObj === "object") {
        const foundRate = getRateForSelectedLocality(ratesObj, selectedLocation, selectedLocality, codeTypeName, master.codeTypeId);
        if (foundRate !== null && !isNaN(foundRate)) {
          codeTypePct = foundRate;
        }
      }

      let price = item.price;
      if (selectedLocality || price === undefined || price === null) {
        price = Math.round((baseStandardPrice * (codeTypePct / 100)) * 100) / 100;
      }

      allCodes.push({
        ...master,
        ...item,
        id: item.id || master?.id || item.code,
        originalMasterId: master?.id || item.id,
        providerOverrideId: item.id,
        code: item.code,
        codeType: codeTypeObj || master?.codeType,
        codeTypeName,
        description: item.description || master?.description || master?.codeShortDesc || item.code,
        price,
        status: item.status || "active",
        currentPct: codeTypePct
      });
      processedCodes.add(item.code);
    });

    return allCodes;
  }, [rawMasterCodes, providerCptCodes, effectiveMedicareRate, selectedLocality, user]);

  // CPT Code Category Options (Billing Code Categories from DB)
  const masterCptCategoryOptions = useMemo(() => {
    const catsSet = new Set<string>();

    if (Array.isArray(cptCategoriesData)) {
      cptCategoriesData.forEach((cat: any) => {
        const name = typeof cat === "string" ? cat : cat?.name;
        if (name) catsSet.add(name);
      });
    }

    (cptCodes || []).forEach((c: any) => {
      const cat = typeof c.category === "object" && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");
      if (cat) catsSet.add(cat);
    });

    if (catsSet.size === 0) {
      [
        "Anesthesia", "Dentistry", "Diagnostic Cardiology", "Evaluation & Management",
        "General", "Medicine", "Pathology", "Physical Therapy", "Radiology", "Surgery"
      ].forEach(c => catsSet.add(c));
    }

    return Array.from(catsSet).sort();
  }, [cptCategoriesData, cptCodes]);

  // CPT Code Subcategory Options (Billing Code Subcategories from DB)
  const masterCptSubcategoryOptions = useMemo(() => {
    const selectedCat = form.category || "";
    const subsSet = new Set<string>();

    if (Array.isArray(cptSubcategoriesData)) {
      cptSubcategoriesData.forEach((s: any) => {
        const parentCat = typeof s.parentCategory === "object" ? s.parentCategory?.name : (s.parentCategory || s.categoryName || "");
        if (selectedCat && parentCat && parentCat.toLowerCase() !== selectedCat.toLowerCase()) {
          return;
        }
        const subName = typeof s === "string" ? s : s?.name || s?.subCategoryName;
        if (subName) subsSet.add(subName);
      });
    }

    (cptCodes || []).forEach((c: any) => {
      const cat = typeof c.category === "object" && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");
      if (selectedCat && cat && cat.toLowerCase() !== selectedCat.toLowerCase()) {
        return;
      }
      const sub = typeof c.subCategory === "object" && c.subCategory !== null
        ? (c.subCategory.name || c.subCategory.code || "")
        : String(c.subCategory || c.subcategory || "");
      if (sub) subsSet.add(sub);
    });

    return Array.from(subsSet).sort();
  }, [cptSubcategoriesData, cptCodes, form.category]);

  // Auto-sync form.category & form.categoryType with CPT code categories
  useEffect(() => {
    if (masterCptCategoryOptions.length > 0 && (!form.category || !masterCptCategoryOptions.includes(form.category))) {
      const firstCat = masterCptCategoryOptions[0];
      setForm(prev => ({ ...prev, category: firstCat }));
    }
  }, [masterCptCategoryOptions, form.category]);

  useEffect(() => {
    if (masterCptSubcategoryOptions.length > 0 && (!form.categoryType || !masterCptSubcategoryOptions.includes(form.categoryType))) {
      setForm(prev => ({ ...prev, categoryType: masterCptSubcategoryOptions[0] }));
    }
  }, [masterCptSubcategoryOptions, form.categoryType]);

  const providerCodeTypes = user?.provider?.codeTypes || user?.codeTypes || [];
  const selectedCodeTypeNames = useMemo(() => {
    if (!Array.isArray(providerCodeTypes) || providerCodeTypes.length === 0) return [];
    return providerCodeTypes.map((ct: any) => {
      if (!ct) return "";
      let nameStr = "";
      if (typeof ct === "string") {
        nameStr = ct;
        if (rawMasterCodes && Array.isArray(rawMasterCodes)) {
          const foundMaster = rawMasterCodes.find((m: any) => m.codeType?.id === ct || m.codeTypeId === ct || m.codeType?.name === ct);
          if (foundMaster?.codeType?.name) {
            nameStr = foundMaster.codeType.name;
          }
        }
      } else if (typeof ct === "object" && ct !== null) {
        nameStr = ct.name || ct.code || ct.id || "";
      } else {
        nameStr = String(ct || "");
      }
      if (typeof nameStr !== "string") nameStr = String(nameStr || "");
      return nameStr.trim().toUpperCase();
    }).filter(Boolean);
  }, [providerCodeTypes, rawMasterCodes]);

  const availableCodeTypes = useMemo(() => {
    if (selectedCodeTypeNames.length > 0) {
      return selectedCodeTypeNames.map(x => String(x));
    }
    const types = new Set<string>();
    (rawMasterCodes || []).forEach((m: any) => {
      let typeName = "";
      if (typeof m.codeType === "string") {
        typeName = m.codeType;
      } else if (typeof m.codeType === "object" && m.codeType !== null) {
        typeName = m.codeType.name || m.codeType.code || "";
      }
      if (typeName && typeof typeName === "string") {
        types.add(typeName);
      }
    });
    return Array.from(types).sort();
  }, [rawMasterCodes, selectedCodeTypeNames]);

  const popupCategoryOptions = useMemo(() => {
    const cats = new Set<string>();
    (cptCodes || []).forEach((c: any) => {
      const cat = typeof c.category === 'object' && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");
      if (cat) cats.add(cat);
    });
    const ALL_10_CATS = [
      "Anesthesia", "Dentistry", "Diagnostic Cardiology", "Evaluation & Management",
      "General", "Medicine", "Pathology", "Physical Therapy", "Radiology", "Surgery"
    ];
    ALL_10_CATS.forEach(cat => cats.add(cat));
    return Array.from(cats).sort();
  }, [cptCodes]);

  const popupSubCategoryOptions = useMemo(() => {
    const subs = new Set<string>();
    (cptCodes || []).forEach((c: any) => {
      const cat = typeof c.category === 'object' && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");
      if (popupCategory !== "all" && cat.toLowerCase() !== popupCategory.toLowerCase()) {
        return;
      }
      const sub = typeof c.subCategory === 'object' && c.subCategory !== null
        ? (c.subCategory.name || c.subCategory.code || "")
        : String(c.subCategory || c.subcategory || "");
      if (sub) subs.add(sub);
    });
    return Array.from(subs).sort();
  }, [cptCodes, popupCategory]);

  const getAdjustedCptPrice = (cptCodeObj: any, id: string) => {
    const basePrice = Number(cptCodeObj?.price || 0);
    const adj = cptAdjustments[id];
    if (!adj) return basePrice;

    if (adj.adjustedPrice !== undefined && !isNaN(adj.adjustedPrice)) {
      return adj.adjustedPrice;
    }
    if (adj.fixed !== undefined && adj.fixed > 0) {
      return Math.max(0, Math.round((basePrice - adj.fixed) * 100) / 100);
    }
    if (adj.percent !== undefined && adj.percent > 0) {
      return Math.max(0, Math.round((basePrice * (1 - Math.min(100, adj.percent) / 100)) * 100) / 100);
    }
    if (!adj.value || adj.value <= 0) return basePrice;

    if (adj.type === "percent") {
      const discounted = basePrice * (1 - Math.min(100, Math.max(0, adj.value)) / 100);
      return Math.max(0, Math.round(discounted * 100) / 100);
    } else if (adj.type === "fixed") {
      const discounted = basePrice - adj.value;
      return Math.max(0, Math.round(discounted * 100) / 100);
    }

    return basePrice;
  };

  const calculatedTotal = useMemo(() => {
    const cptTotal = selectedCpts.reduce((sum, id) => {
      const code = cptCodes?.find((c: any) => c.id === id || c.originalMasterId === id || c.providerOverrideId === id);
      const unit = cptUnits[id] || 1;
      const adjustedPrice = getAdjustedCptPrice(code, id);
      return sum + (adjustedPrice * unit);
    }, 0);
    const customTotal = customItems.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.unit) || 1)), 0);
    return cptTotal + customTotal;
  }, [selectedCpts, cptCodes, cptUnits, cptAdjustments, customItems]);

  const prevCalcTotalRef = useRef(0);

  useEffect(() => {
    if (!open) {
      prevCalcTotalRef.current = 0;
      return;
    }

    if (calculatedTotal !== prevCalcTotalRef.current) {
      const isInitialEditLoad = editId && prevCalcTotalRef.current === 0 && calculatedTotal > 0;

      if (!isInitialEditLoad) {
        setForm(prev => {
          const discount = Number(prev.discountPercent || 0);
          return {
            ...prev,
            finalPrice: calculatedTotal ? (calculatedTotal * (1 - discount / 100)).toFixed(2) : ""
          };
        });
      }

      prevCalcTotalRef.current = calculatedTotal;
    }
  }, [calculatedTotal, open, editId]);

  const handleSubmit = () => {
    let finalP = calculatedTotal * (1 - Number(form.discountPercent) / 100);
    let finalD = Number(form.discountPercent);

    if (form.finalPrice) {
      finalP = Number(form.finalPrice);
      finalD = calculatedTotal ? ((calculatedTotal - finalP) / calculatedTotal * 100) : 0;
    }

    const matchedLookup = (rawBundleCategoriesData || []).find(
      (c: any) => (c.categoryName || "").toLowerCase() === (form.category || "").toLowerCase() &&
                  (c.categoryType || "").toLowerCase() === (form.categoryType || "").toLowerCase()
    );

    const payload: any = {
      name: form.name,
      description: form.description,
      category: form.category,
      categoryType: form.categoryType,
      bundleCategoryId: matchedLookup?.id || undefined,
      status: form.status,
      cptCodeIds: selectedCpts,
      cptUnits: {
        ...cptUnits,
        _adjustments: cptAdjustments
      },
      cptAdjustments,
      totalPrice: calculatedTotal,
      finalPrice: finalP,
      discountPercent: parseFloat(finalD.toFixed(2)),
      customItems,
      thumbnailUrl,
      tags: selectedTags,
      isSubscription,
      subscriptionInterval: isSubscription ? subscriptionInterval : undefined,
      recurringAmount: isSubscription && recurringAmount ? parseFloat(recurringAmount) : undefined,
      billingCycles: isSubscription && billingCycles ? parseInt(billingCycles, 10) : undefined,
      type: isGroup ? (bundleScope === "GROUP" ? "GROUP" : "PROVIDER") : "INDIVIDUAL",
      participatingProviderIds: isGroup ? participatingProviderIds : undefined,
      participatingProviders: isGroup ? {
        isAll: bundleScope === "GROUP" || (myLinkedProviders.length > 0 && participatingProviderIds.length === myLinkedProviders.length),
        providerIds: participatingProviderIds
      } : undefined
    };

    if (editId) {
      updateBundle.mutate({ id: editId, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
          toast({ title: "Bundle updated", description: `${form.name} has been updated.` });
          setOpen(false);
          setEditId(null);
          setCptUnits({});
          setCptAdjustments({});
          setThumbnailUrl(null);
          setSelectedTags([]);
          setTagInput("");
          setIsSubscription(false);
          setSubscriptionInterval("MONTHLY");
          setRecurringAmount("");
          setBillingCycles("");
          fetchExistingTags();
        }
      });
    } else {
      createBundle.mutate({ data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
          toast({ title: "Bundle created", description: `${form.name} has been added.` });
          setOpen(false);
          setForm({ name: "", description: "", category: "Medical", categoryType: "Office Visits", discountPercent: "0", finalPrice: "", status: "active" });
          setSelectedCpts([]);
          setCptUnits({});
          setCptAdjustments({});
          setCustomItems([]);
          setCustomName("");
          setCustomDesc("");
          setCustomPrice("");
          setCustomUnit("1");
          setThumbnailUrl(null);
          setSelectedTags([]);
          setTagInput("");
          fetchExistingTags();
          setBundleScope("GROUP");
          setParticipatingProviderIds([]);
          setDisclaimerAccepted(false);
          navigatePostSaveBundle(setLocation);
        }
      });
    }
  };

  const parseBundleTags = (bundle: any): string[] => {
    if (!bundle) return [];
    const raw = bundle.tags || bundle.searchTags || bundle.filterTags || bundle.keywords;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.map((t: any) => String(t).trim().toLowerCase().replace(/^#/, '')).filter(Boolean);
    }
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((t: any) => String(t).trim().toLowerCase().replace(/^#/, '')).filter(Boolean);
        }
      } catch (e) {}
      return raw
        .split(/[,;\s]+/)
        .map((t: string) => t.trim().toLowerCase().replace(/^#/, ''))
        .filter(Boolean);
    }
    return [];
  };

  const handleEdit = (bundle: BundleWithCpts) => {
    setEditId(bundle.id);
    setForm({
      name: bundle.name,
      description: bundle.description || "",
      category: bundle.category || "Medical",
      categoryType: (bundle as any).categoryType || "Office Visits",
      discountPercent: bundle.discountPercent.toString(),
      finalPrice: bundle.finalPrice.toString(),
      status: bundle.status || "active"
    });
    setSelectedCpts(bundle.cptCodes?.map(c => c.id) || []);
    setSelectedTags(parseBundleTags(bundle));
    setTagInput("");
    setIsSubscription(Boolean((bundle as any).isSubscription));
    setSubscriptionInterval((bundle as any).subscriptionInterval || "MONTHLY");
    setRecurringAmount((bundle as any).recurringAmount !== undefined && (bundle as any).recurringAmount !== null ? String((bundle as any).recurringAmount) : "");
    setBillingCycles((bundle as any).billingCycles !== undefined && (bundle as any).billingCycles !== null ? String((bundle as any).billingCycles) : "");
    const rawUnits = (bundle as any).cptUnits || {};
    setCptUnits(rawUnits);
    const loadedAdjustments = (bundle as any).cptAdjustments || rawUnits._adjustments || {};
    setCptAdjustments(loadedAdjustments);
    setCustomItems(Array.isArray((bundle as any).customItems) ? (bundle as any).customItems : []);
    setCustomName("");
    setCustomDesc("");
    setCustomPrice("");
    setCustomUnit("1");
    setThumbnailUrl(bundle.thumbnailUrl || null);
    setSelectedCodeType("all");
    const isProviderSpecific = bundle.type?.startsWith('PROVIDER_');
    const pProviders = (bundle as any).participatingProviders;

    if (pProviders && Array.isArray(pProviders.providerIds) && pProviders.providerIds.length > 0) {
      setParticipatingProviderIds(pProviders.providerIds.map(String));
      setBundleScope(pProviders.isAll ? "GROUP" : "PROVIDER");
    } else if (isProviderSpecific) {
      const pId = bundle.type!.split('_')[1];
      const matchedLp = myLinkedProviders.find((lp: any) => String(lp.providerId) === String(pId) || String(lp.id) === String(pId));
      setParticipatingProviderIds(matchedLp ? [String(matchedLp.providerId || matchedLp.id)] : [pId]);
      setBundleScope("PROVIDER");
    } else if (bundle.type === 'GROUP') {
      setParticipatingProviderIds(myLinkedProviders.map((p: any) => String(p.providerId || p.id)).filter(Boolean));
      setBundleScope("GROUP");
    } else {
      setParticipatingProviderIds([]);
      setBundleScope("GROUP");
    }

    setDisclaimerAccepted(false);
    setOpen(true);
  };

  const handleDuplicate = (bundle: BundleWithCpts) => {
    setEditId(null);
    setForm({
      name: `${bundle.name} (Copy)`,
      description: bundle.description || "",
      category: bundle.category || "Medical",
      categoryType: (bundle as any).categoryType || "Office Visits",
      discountPercent: bundle.discountPercent.toString(),
      finalPrice: bundle.finalPrice.toString(),
      status: "active"
    });
    setSelectedCpts(bundle.cptCodes?.map(c => c.id) || []);
    setSelectedTags(parseBundleTags(bundle));
    setTagInput("");
    setIsSubscription(Boolean((bundle as any).isSubscription));
    setSubscriptionInterval((bundle as any).subscriptionInterval || "MONTHLY");
    setRecurringAmount((bundle as any).recurringAmount !== undefined && (bundle as any).recurringAmount !== null ? String((bundle as any).recurringAmount) : "");
    setBillingCycles((bundle as any).billingCycles !== undefined && (bundle as any).billingCycles !== null ? String((bundle as any).billingCycles) : "");
    const rawUnits = (bundle as any).cptUnits || {};
    setCptUnits(rawUnits);
    const loadedAdjustments = (bundle as any).cptAdjustments || rawUnits._adjustments || {};
    setCptAdjustments(loadedAdjustments);
    setCustomItems(Array.isArray((bundle as any).customItems) ? (bundle as any).customItems : []);
    setCustomName("");
    setCustomDesc("");
    setCustomPrice("");
    setCustomUnit("1");
    setThumbnailUrl(bundle.thumbnailUrl || null);
    setSelectedCodeType("all");
    const isProviderSpecific = bundle.type?.startsWith('PROVIDER_');
    const pProviders = (bundle as any).participatingProviders;

    if (pProviders && Array.isArray(pProviders.providerIds) && pProviders.providerIds.length > 0) {
      setParticipatingProviderIds(pProviders.providerIds.map(String));
      setBundleScope(pProviders.isAll ? "GROUP" : "PROVIDER");
    } else if (isProviderSpecific) {
      const pId = bundle.type!.split('_')[1];
      const matchedLp = myLinkedProviders.find((lp: any) => String(lp.providerId) === String(pId) || String(lp.id) === String(pId));
      setParticipatingProviderIds(matchedLp ? [String(matchedLp.providerId || matchedLp.id)] : [pId]);
      setBundleScope("PROVIDER");
    } else if (bundle.type === 'GROUP') {
      setParticipatingProviderIds(myLinkedProviders.map((p: any) => String(p.providerId || p.id)).filter(Boolean));
      setBundleScope("GROUP");
    } else {
      setParticipatingProviderIds([]);
      setBundleScope("GROUP");
    }

    setDisclaimerAccepted(false);
    setOpen(true);
    toast({
      title: "Bundle Duplicated",
      description: `Copied "${bundle.name}". Edit details and click Create Bundle.`
    });
  };

  const handleDelete = () => {
    if (!deleteId) return;
    const isPermanent = activeTab === "deleted";
    deleteBundle.mutate({ id: deleteId, query: isPermanent ? { permanent: "true" } : undefined } as any, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
        const isArchived = res?.status === 'archived';
        const isPerm = res?.status === 'permanently_deleted';
        toast({
          title: isPerm ? "Bundle permanently deleted" : isArchived ? "Bundle archived" : "Bundle deleted",
          description: res?.message || `${deleteName} has been ${isPerm ? 'permanently deleted from database' : isArchived ? 'archived' : 'moved to Deleted tab'}.`
        });
        setDeleteId(null);
        setDeleteName("");
      },
      onError: (err: any) => {
        toast({ title: "Delete failed", description: err.message || "Failed to delete bundle", variant: "destructive" });
        setDeleteId(null);
        setDeleteName("");
      }
    });
  };

  const handleDeleteClick = (bundle: BundleWithCpts) => {
    const appointmentCount = bundle._count?.appointments || 0;
    if (appointmentCount > 0) {
      setArchiveConfirmBundle(bundle);
    } else {
      setDeleteId(bundle.id);
      setDeleteName(bundle.name);
    }
  };

  const toggleCpt = (c: any) => {
    setSelectedCpts(prev => {
      const exists = prev.some(id => id === c.id || id === c.originalMasterId || id === c.providerOverrideId);
      if (exists) {
        return prev.filter(id => id !== c.id && id !== c.originalMasterId && id !== c.providerOverrideId);
      }
      return [...prev, c.id];
    });
  };



  return (
    <ProviderLayout>
      <div className="w-full pt-4 sm:pt-6 lg:pt-8 flex flex-col min-h-[calc(100vh-8rem)]">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Service Bundles</h1>
              <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">Group CPT codes into discounted service packages</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
            <div className="flex items-center gap-1 bg-muted/80 p-1 rounded-xl border border-border">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${viewMode === "list"
                    ? "bg-background text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <List className="w-4 h-4" /> List
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${viewMode === "grid"
                    ? "bg-background text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <LayoutGrid className="w-4 h-4" /> Grid
              </button>
            </div>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-44 h-10 rounded-xl text-xs font-semibold bg-background border border-border">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="custom">Custom Order</SelectItem>
                <SelectItem value="name_asc">Name (A–Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z–A)</SelectItem>
                <SelectItem value="price_asc">Price (Low to High)</SelectItem>
                <SelectItem value="price_desc">Price (High to Low)</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={() => setLocation("/provider/bundles/create")}
              className="bg-gradient-to-r from-primary to-secondary text-white border-0 rounded-xl gap-2 shadow-sm"
              data-testid="button-add-bundle"
            >
              <Plus className="w-4 h-4" /> Create Bundle
            </Button>

            <Dialog open={open} onOpenChange={(val) => {
              setOpen(val);
              if (!val) {
                setEditId(null);
                setForm({ name: "", description: "", category: "Medical", categoryType: "Office Visits", discountPercent: "0", finalPrice: "", status: "active" });
                setSelectedCpts([]);
                setBundleScope("GROUP");
                setParticipatingProviderIds(myLinkedProviders.map((p: any) => String(p.providerId || p.id)).filter(Boolean));
                setDisclaimerAccepted(false);
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditId(null);
                    setForm({ name: "", description: "", category: "Medical", categoryType: "Office Visits", discountPercent: "0", finalPrice: "", status: "active" });
                    setCustomItems([]);
                    setSelectedCpts([]);
                    setThumbnailUrl(null);
                    setSelectedCodeType("all");
                    setBundleScope("GROUP");
                    setDisclaimerAccepted(false);
                    setParticipatingProviderIds(myLinkedProviders.map((p: any) => String(p.providerId || p.id)).filter(Boolean));
                    setSelectedTags([]);
                    setTagInput("");
                  }}
                  className="rounded-xl gap-1 text-xs font-semibold"
                  data-testid="button-quick-edit-bundle"
                >
                  <Edit2 className="w-3.5 h-3.5" /> Quick Edit Popup
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                <DialogHeader className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-border">
                  <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-900/80 p-1 rounded-xl border border-slate-200 dark:border-slate-800">
                    {/* 1. Active / Inactive Toggle Icon */}
                    <button
                      type="button"
                      onClick={() => {
                        setForm(prev => ({
                          ...prev,
                          status: prev.status === "active" ? "inactive" : "active"
                        }));
                      }}
                      title={
                        form.status === "active"
                          ? "Status: Active (Click to set Inactive)"
                          : "Status: Inactive (Click to set Active)"
                      }
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                        form.status === "active"
                          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25"
                          : form.status === "inactive"
                          ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500/25"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                      data-testid="button-toggle-active-status"
                    >
                      {form.status === "active" ? (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                          <span>Active</span>
                        </>
                      ) : form.status === "inactive" ? (
                        <>
                          <XCircle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                          <span>Inactive</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5 opacity-50" />
                          <span>Activate</span>
                        </>
                      )}
                    </button>

                    {/* 2. Archive / UnArchive Toggle Icon */}
                    <button
                      type="button"
                      onClick={() => {
                        setForm(prev => ({
                          ...prev,
                          status: prev.status === "archived" ? "active" : "archived"
                        }));
                      }}
                      title={
                        form.status === "archived"
                          ? "Status: Archived (Click to Unarchive)"
                          : "Archive Bundle"
                      }
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                        form.status === "archived"
                          ? "bg-purple-500/15 text-purple-700 dark:text-purple-400 border border-purple-500/30 hover:bg-purple-500/25"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
                      }`}
                      data-testid="button-toggle-archive-status"
                    >
                      {form.status === "archived" ? (
                        <>
                          <ArchiveRestore className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                          <span>Archived</span>
                        </>
                      ) : (
                        <>
                          <Archive className="w-3.5 h-3.5" />
                          <span>Archive</span>
                        </>
                      )}
                    </button>

                    {/* 3. Delete Icon */}
                    <button
                      type="button"
                      onClick={() => {
                        if (editId) {
                          const currentEditingBundle = (bundles ?? []).find((b: any) => b.id === editId);
                          if (currentEditingBundle) {
                            setOpen(false);
                            handleDeleteClick(currentEditingBundle);
                          } else {
                            setOpen(false);
                          }
                        } else {
                          setOpen(false);
                        }
                      }}
                      title="Delete Bundle"
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 border border-transparent hover:border-rose-200 dark:hover:border-rose-900 transition-all"
                      data-testid="button-delete-bundle-dialog"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Delete</span>
                    </button>

                    {/* 4. Duplicate / Copy Icon */}
                    {editId && (
                      <button
                        type="button"
                        onClick={() => {
                          const currentEditingBundle = (bundles ?? []).find((b: any) => b.id === editId);
                          if (currentEditingBundle) {
                            handleDuplicate(currentEditingBundle);
                          }
                        }}
                        title="Duplicate / Copy into New Bundle"
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-900 transition-all"
                        data-testid="button-duplicate-bundle-dialog"
                      >
                        <Copy className="w-3.5 h-3.5" />
                        <span>Duplicate</span>
                      </button>
                    )}
                  </div>

                  <DialogTitle className="text-lg font-bold">
                    {editId ? "Edit Service Bundle" : "Create Service Bundle"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-3 max-h-[70vh] overflow-y-auto px-1 -mx-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {/* Thumbnail Image Upload */}
                  <div>
                    <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Thumbnail Image</Label>
                    <div className="flex items-center gap-4">
                      {thumbnailUrl ? (
                        <div className="relative h-16 w-16 rounded-xl overflow-hidden border border-border group bg-muted/30">
                          <img src={getImageUrl(thumbnailUrl)} alt="Thumbnail preview" className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setThumbnailUrl(null)}
                            className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove Image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="h-16 w-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center text-muted-foreground bg-muted/20">
                          <Package className="h-6 w-6 opacity-40" />
                        </div>
                      )}
                      <div>
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-white dark:bg-slate-900 text-xs font-semibold text-foreground cursor-pointer hover:bg-muted transition-colors shadow-2xs">
                          {uploadingImage ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <UploadCloud className="h-3.5 w-3.5 text-muted-foreground" />}
                          {uploadingImage ? "Uploading..." : thumbnailUrl ? "Change Image" : "Upload Thumbnail"}
                          <input type="file" accept="image/*" className="hidden" onChange={handleThumbnailUpload} disabled={uploadingImage} />
                        </label>
                        <p className="text-[10px] text-muted-foreground mt-1">PNG, JPG, WEBP up to 5MB. Stored in uploads/thumbnail.</p>
                      </div>
                    </div>
                  </div>

                  <div><Label>Bundle Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Annual Wellness Package" className="mt-1 rounded-xl" data-testid="input-bundle-name" /></div>
                  <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Comprehensive annual care..." className="mt-1 rounded-xl" /></div>

                  {/* Tag Cloud Component */}
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5 text-primary" /> Bundle Tags
                      </Label>
                      {selectedTags.length > 0 && (
                        <span className="text-[11px] font-medium text-muted-foreground">
                          {selectedTags.length} tag{selectedTags.length > 1 ? 's' : ''} attached
                        </span>
                      )}
                    </div>

                    {/* Active Selected Tags */}
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 bg-muted/20 border border-border rounded-xl">
                        {selectedTags.map(tag => (
                          <span key={tag} className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 px-2.5 py-0.5 rounded-full text-xs font-medium">
                            #{tag}
                            <button
                              type="button"
                              onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                              className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Tag Input */}
                    <div className="flex gap-2">
                      <Input
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            const clean = tagInput.trim().toLowerCase().replace(/^#/, '');
                            if (clean && !selectedTags.includes(clean)) {
                              setSelectedTags(prev => [...prev, clean]);
                              if (!existingTags.includes(clean)) setExistingTags(prev => [...prev, clean].sort());
                            }
                            setTagInput('');
                          }
                        }}
                        placeholder="Type a tag & press Enter or comma..."
                        className="rounded-xl text-xs"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const clean = tagInput.trim().toLowerCase().replace(/^#/, '');
                          if (clean && !selectedTags.includes(clean)) {
                            setSelectedTags(prev => [...prev, clean]);
                            if (!existingTags.includes(clean)) setExistingTags(prev => [...prev, clean].sort());
                          }
                          setTagInput('');
                        }}
                        className="rounded-xl text-xs px-3"
                      >
                        Add
                      </Button>
                    </div>

                    {/* Previously Used Tag Cloud */}
                    {existingTags.length > 0 && (
                      <div className="pt-1">
                        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Previously Used Tags (click to toggle):</p>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1">
                          {existingTags.map(tag => {
                            const isSelected = selectedTags.includes(tag);
                            return (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedTags(selectedTags.filter(t => t !== tag));
                                  } else {
                                    setSelectedTags([...selectedTags, tag]);
                                  }
                                }}
                                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                                  isSelected
                                    ? 'bg-primary text-primary-foreground border-primary shadow-2xs'
                                    : 'bg-background hover:bg-muted text-muted-foreground border-border hover:border-primary/40'
                                }`}
                              >
                                #{tag} {isSelected && '✓'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>



                  <div className="space-y-3">
                    <div>
                      <Label>Location</Label>
                      <Select value={selectedLocationId} onValueChange={v => setSelectedLocationId(v)}>
                        <SelectTrigger className="mt-1 rounded-xl bg-white dark:bg-slate-950">
                          <SelectValue placeholder="Select Location" />
                        </SelectTrigger>
                        <SelectContent>
                          {providerLocations.map((loc: any) => (
                            <SelectItem key={loc.id} value={String(loc.id)}>
                              {loc.displayLabel}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {isGroup && (
                      <div>
                        <Label>Bundle Type</Label>
                        <Select value={bundleScope} onValueChange={(v: "GROUP" | "PROVIDER") => {
                          setBundleScope(v);
                          if (v === "GROUP") {
                            setParticipatingProviderIds(myLinkedProviders.map((p: any) => String(p.providerId || p.id)).filter(Boolean));
                          }
                        }}>
                          <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GROUP">Group Bundle (All Providers)</SelectItem>
                            <SelectItem value="PROVIDER">Provider Specific Bundle</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isGroup && (
                      <div className="space-y-2 pt-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-semibold text-foreground">Select Included Providers</Label>
                          <span className="text-[11px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                            {participatingProviderIds.length} of {myLinkedProviders.length} Selected
                          </span>
                        </div>

                        <div className="border border-border rounded-xl p-3 bg-muted/20 space-y-2 max-h-48 overflow-y-auto">
                          <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
                            <label className="flex items-center gap-2 text-xs font-bold text-foreground cursor-pointer">
                              <input
                                type="checkbox"
                                checked={participatingProviderIds.length === myLinkedProviders.length && myLinkedProviders.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setParticipatingProviderIds(myLinkedProviders.map((p: any) => String(p.providerId || p.id)).filter(Boolean));
                                  } else {
                                    setParticipatingProviderIds([]);
                                  }
                                }}
                                className="rounded text-primary focus:ring-primary h-4 w-4"
                              />
                              Select All Providers ({myLinkedProviders.length})
                            </label>
                          </div>

                          {myLinkedProviders.map((p: any) => {
                            const pId = String(p.providerId || p.id);
                            const isChecked = participatingProviderIds.includes(pId);

                            return (
                              <label key={pId} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors text-xs">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setParticipatingProviderIds(prev => [...prev, pId]);
                                      } else {
                                        setParticipatingProviderIds(prev => prev.filter(id => id !== pId));
                                      }
                                    }}
                                    className="rounded text-primary focus:ring-primary h-4 w-4"
                                  />
                                  <span className="font-semibold text-foreground">{p.name}</span>
                                </div>
                                {p.npiNumber && <span className="text-[11px] text-muted-foreground font-mono">NPI: {p.npiNumber}</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="block">Include CPT Codes</Label>
                        {(popupCodeType !== "all" || popupCategory !== "all" || popupSubCategory !== "all") && (
                          <button
                            type="button"
                            onClick={() => {
                              setPopupCodeType("all");
                              setPopupCategory("all");
                              setPopupSubCategory("all");
                            }}
                            className="text-[11px] font-semibold text-primary hover:underline"
                          >
                            Reset CPT Filters
                          </button>
                        )}
                      </div>

                      {/* POPUP FILTERS: Code Type, Category, Sub Category */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2 p-2.5 bg-slate-50/80 dark:bg-slate-900/80 rounded-xl border border-slate-200/80 dark:border-slate-800">
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Code Type</label>
                          <Select value={popupCodeType} onValueChange={(v) => setPopupCodeType(v)}>
                            <SelectTrigger className="h-8 text-xs rounded-lg border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2.5 font-medium shadow-2xs">
                              <SelectValue placeholder="All Code Types" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value="all" className="text-xs">All Code Types</SelectItem>
                              {availableCodeTypes.map((type: string) => (
                                <SelectItem key={type} value={type} className="text-xs">{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Category</label>
                          <Select
                            value={popupCategory}
                            onValueChange={(v) => {
                              setPopupCategory(v);
                              setPopupSubCategory("all");
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs rounded-lg border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2.5 font-medium shadow-2xs">
                              <SelectValue placeholder="All Categories" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value="all" className="text-xs">All Categories</SelectItem>
                              {popupCategoryOptions.map((cat: string) => (
                                <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Sub Category</label>
                          <Select value={popupSubCategory} onValueChange={(v) => setPopupSubCategory(v)}>
                            <SelectTrigger className="h-8 text-xs rounded-lg border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2.5 font-medium shadow-2xs">
                              <SelectValue placeholder="All Sub Categories" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value="all" className="text-xs">All Sub Categories</SelectItem>
                              {popupSubCategoryOptions.map((sub: string) => (
                                <SelectItem key={sub} value={sub} className="text-xs">{sub}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Popover open={cptOpen} onOpenChange={setCptOpen} modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={cptOpen}
                            className="w-full justify-between rounded-xl bg-slate-50 border-slate-200 font-normal hover:bg-slate-100"
                          >
                            <span className={selectedCpts.length === 0 ? "text-muted-foreground" : "text-foreground"}>
                              {selectedCpts.length === 0
                                ? "Search or select CPT codes to add..."
                                : `${selectedCpts.length} code${selectedCpts.length > 1 ? 's' : ''} selected`}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border-slate-200 shadow-xl overflow-hidden" align="start" sideOffset={8}>
                          <Command className="bg-white dark:bg-slate-950">
                            <CommandInput placeholder="Search code or description..." value={cptSearch} onValueChange={setCptSearch} className="h-11" />
                            <CommandList className="max-h-[280px]">
                              <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">No CPT code found matching filters.</CommandEmpty>
                              <CommandGroup className="p-1.5">
                                {(cptCodes ?? []).filter((c: any) => {
                                  if (c.status !== "active") return false;
                                  const codeTypeName = (c.codeTypeName || c.codeType?.name || (typeof c.codeType === "string" ? c.codeType : "") || "").trim();
                                  
                                  // Code Type filter
                                  if (popupCodeType !== "all" && codeTypeName.toLowerCase() !== popupCodeType.toLowerCase()) {
                                    return false;
                                  }
                                  if (selectedCodeTypeNames.length > 0) {
                                    if (!selectedCodeTypeNames.map(x => String(x).toUpperCase()).includes(codeTypeName.toUpperCase())) return false;
                                  }

                                  // Category filter
                                  const catName = typeof c.category === 'object' && c.category !== null
                                    ? (c.category.name || c.category.code || "")
                                    : String(c.category || "");
                                  if (popupCategory !== "all" && catName.toLowerCase() !== popupCategory.toLowerCase()) {
                                    return false;
                                  }

                                  // SubCategory filter
                                  const subCatName = typeof c.subCategory === 'object' && c.subCategory !== null
                                    ? (c.subCategory.name || c.subCategory.code || "")
                                    : String(c.subCategory || c.subcategory || "");
                                  if (popupSubCategory !== "all" && subCatName.toLowerCase() !== popupSubCategory.toLowerCase()) {
                                    return false;
                                  }

                                  return true;
                                }).map((c: any) => {
                                  const isSelected = selectedCpts.some(id => id === c.id || id === c.originalMasterId || id === c.providerOverrideId);
                                  return (
                                    <CommandItem
                                      key={c.id}
                                      value={`${c.code} ${c.description}`}
                                      onSelect={() => toggleCpt(c)}
                                      className={`flex items-center justify-between cursor-pointer py-2.5 px-3 rounded-lg mb-0.5 last:mb-0 transition-colors ${isSelected ? "bg-slate-50 dark:bg-slate-900/50" : ""}`}
                                    >
                                      <div className="flex items-center gap-3 overflow-hidden min-w-0 flex-1 pr-3">
                                        <div className={`w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900'}`}>
                                          {isSelected && <Check className="h-3 w-3 stroke-[3]" />}
                                        </div>
                                        <div className="truncate flex items-center gap-2">
                                          <span className="font-mono text-primary font-bold">{c.code}</span>
                                          {c.category && (
                                            <Badge className="bg-cyan-500 hover:bg-cyan-600 text-white text-[10px] h-4 px-1.5 font-medium rounded-full shrink-0 border-none">
                                              {typeof c.category === 'object' && c.category !== null
                                                ? ((c.category as any)?.name || (c.category as any)?.code || "")
                                                : String(c.category || "")}
                                            </Badge>
                                          )}
                                          <span className="text-muted-foreground truncate">{c.description}</span>
                                          <Badge variant="secondary" className="text-[10px] h-4 px-1.5 font-medium bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 border-none">
                                            {c.currentPct}%
                                          </Badge>
                                        </div>
                                      </div>
                                      <span className="font-semibold shrink-0 ml-3 text-slate-700 dark:text-slate-300">${c.price.toFixed(2)}</span>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>

                      {selectedCpts.length > 0 && (
                        <div className="mt-3 space-y-2.5 max-h-72 overflow-y-auto pr-1">
                          {selectedCpts.map(id => {
                            const c = cptCodes?.find((c: any) => c.id === id || c.originalMasterId === id || c.providerOverrideId === id);
                            if (!c) return null;
                            const unit = cptUnits[id] || 1;
                            const basePrice = Number(c.price || 0);
                            const adj = cptAdjustments[id] || { type: "percent", value: 0 };

                            const currentPercent = adj.percent !== undefined
                              ? adj.percent
                              : adj.type === "percent"
                              ? (adj.value || 0)
                              : basePrice > 0 && (adj.fixed !== undefined || (adj.type === "fixed" && adj.value))
                              ? Math.round((((adj.fixed ?? adj.value) / basePrice) * 100) * 100) / 100
                              : 0;

                            const currentDiscAmt = adj.fixed !== undefined
                              ? adj.fixed
                              : adj.type === "fixed"
                              ? (adj.value || 0)
                              : basePrice > 0 && (adj.percent !== undefined || (adj.type === "percent" && adj.value))
                              ? Math.round((basePrice * ((adj.percent ?? adj.value) / 100)) * 100) / 100
                              : 0;

                            const finalAmount = adj.adjustedPrice !== undefined
                              ? adj.adjustedPrice
                              : Math.max(0, Math.round((basePrice - currentDiscAmt) * 100) / 100);

                            const lineTotal = finalAmount * unit;
                            const isAdjusted = currentPercent > 0 || currentDiscAmt > 0;

                            return (
                              <div key={id} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 text-sm space-y-2">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="truncate flex-1 min-w-0">
                                    <span className="font-mono text-primary font-bold mr-2">{c.code}</span>
                                    <span className="text-slate-700 dark:text-slate-200 font-medium truncate">{c.description}</span>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <div className="text-right">
                                      <div className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                                        ${lineTotal.toFixed(2)}
                                      </div>
                                      {isAdjusted && (
                                        <div className="text-[10px] text-slate-400 line-through">
                                          Standard: ${(basePrice * unit).toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                    <Button variant="ghost" size="icon" className="w-6 h-6 text-slate-400 hover:text-destructive" onClick={() => setSelectedCpts(prev => prev.filter(x => x !== id))}>
                                      <X className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 text-xs">
                                  <div className="flex items-center gap-1.5 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-white dark:bg-slate-900 shadow-2xs">
                                    <span className="text-[11px] text-slate-500 font-semibold">Units:</span>
                                    <input
                                      type="number"
                                      min="1"
                                      value={unit}
                                      onChange={(e) => {
                                        const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                                        setCptUnits(prev => ({ ...prev, [id]: val }));
                                      }}
                                      className="w-8 text-center font-bold bg-transparent focus:outline-none"
                                    />
                                  </div>

                                  <div className="flex items-center gap-2 flex-wrap">
                                    {/* Entry Field 1: Disc % */}
                                    <div className="flex items-center gap-1 bg-white dark:bg-slate-900 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 shadow-2xs">
                                      <span className="text-[10px] font-bold text-slate-500 uppercase">Disc %:</span>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        placeholder="0"
                                        value={currentPercent === 0 && !adj.percent ? "" : currentPercent}
                                        onChange={(e) => {
                                          const valStr = e.target.value;
                                          if (valStr === "") {
                                            setCptAdjustments(prev => ({
                                              ...prev,
                                              [id]: { type: "percent", value: 0, percent: 0, fixed: 0, adjustedPrice: basePrice }
                                            }));
                                            return;
                                          }
                                          const p = Math.max(0, Math.min(100, parseFloat(valStr) || 0));
                                          const discAmt = Math.round((basePrice * (p / 100)) * 100) / 100;
                                          const finalPrice = Math.max(0, Math.round((basePrice - discAmt) * 100) / 100);
                                          setCptAdjustments(prev => ({
                                            ...prev,
                                            [id]: {
                                              type: "percent",
                                              value: p,
                                              percent: p,
                                              fixed: discAmt,
                                              adjustedPrice: finalPrice
                                            }
                                          }));
                                        }}
                                        className="w-14 h-6 text-center text-xs font-bold bg-transparent focus:outline-none text-slate-900 dark:text-slate-100"
                                      />
                                      <span className="text-[11px] font-semibold text-slate-500">%</span>
                                    </div>

                                    {/* Entry Field 2: Disc Amt ($) */}
                                    <div className="flex items-center gap-1 bg-white dark:bg-slate-900 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 shadow-2xs">
                                      <span className="text-[10px] font-bold text-slate-500 uppercase">Disc Amt ($):</span>
                                      <input
                                        type="number"
                                        min="0"
                                        max={basePrice}
                                        step="0.01"
                                        placeholder="0.00"
                                        value={currentDiscAmt === 0 && !adj.fixed ? "" : currentDiscAmt}
                                        onChange={(e) => {
                                          const valStr = e.target.value;
                                          if (valStr === "") {
                                            setCptAdjustments(prev => ({
                                              ...prev,
                                              [id]: { type: "fixed", value: 0, percent: 0, fixed: 0, adjustedPrice: basePrice }
                                            }));
                                            return;
                                          }
                                          const discAmt = Math.max(0, Math.min(basePrice, parseFloat(valStr) || 0));
                                          const p = basePrice > 0 ? Math.round(((discAmt / basePrice) * 100) * 100) / 100 : 0;
                                          const finalPrice = Math.max(0, Math.round((basePrice - discAmt) * 100) / 100);
                                          setCptAdjustments(prev => ({
                                            ...prev,
                                            [id]: {
                                              type: "fixed",
                                              value: discAmt,
                                              percent: p,
                                              fixed: discAmt,
                                              adjustedPrice: finalPrice
                                            }
                                          }));
                                        }}
                                        className="w-16 h-6 text-center text-xs font-bold bg-transparent focus:outline-none text-slate-900 dark:text-slate-100"
                                      />
                                      <span className="text-[10px] font-medium text-slate-400">off</span>
                                    </div>

                                    {/* Entry Field 3: Final Amount ($) */}
                                    <div className="flex items-center gap-1 bg-primary/10 px-2 py-1 rounded-lg border border-primary/20 shadow-2xs">
                                      <span className="text-[10px] font-bold text-primary uppercase">Final Amount ($):</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder={basePrice.toFixed(2)}
                                        value={finalAmount}
                                        onChange={(e) => {
                                          const valStr = e.target.value;
                                          if (valStr === "") {
                                            setCptAdjustments(prev => ({
                                              ...prev,
                                              [id]: { type: "fixed", value: 0, percent: 0, fixed: 0, adjustedPrice: basePrice }
                                            }));
                                            return;
                                          }
                                          const finalPrice = Math.max(0, parseFloat(valStr) || 0);
                                          const discAmt = Math.max(0, Math.round((basePrice - finalPrice) * 100) / 100);
                                          const p = basePrice > 0 ? Math.round(((discAmt / basePrice) * 100) * 100) / 100 : 0;
                                          setCptAdjustments(prev => ({
                                            ...prev,
                                            [id]: {
                                              type: "fixed",
                                              value: discAmt,
                                              percent: p,
                                              fixed: discAmt,
                                              adjustedPrice: finalPrice
                                            }
                                          }));
                                        }}
                                        className="w-16 h-6 text-center text-xs font-bold bg-transparent focus:outline-none text-primary"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* CUSTOM ITEMS SECTION */}
                    <div className="p-4 rounded-xl border border-amber-200/80 bg-amber-50/30 dark:bg-amber-950/10 space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-300">
                          Custom Items / Extra Services ({customItems.length})
                        </Label>
                        <span className="text-xs font-medium text-amber-800 dark:text-amber-400">
                          Custom Total: ${customItems.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.unit) || 1)), 0).toFixed(2)}
                        </span>
                      </div>

                      {customItems.length > 0 && (
                        <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                          {customItems.map((item) => {
                            const u = item.unit || 1;
                            const lineTot = Number(item.price) * u;
                            return (
                              <div
                                key={item.id}
                                className="flex items-center justify-between p-2 rounded-lg border border-amber-200 bg-white dark:bg-slate-900 text-xs"
                              >
                                <div>
                                  <p className="font-semibold text-slate-900 dark:text-slate-100">{item.name}</p>
                                  {item.description && (
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{item.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1 border border-amber-200 dark:border-amber-800 rounded-md px-1.5 py-0.5 bg-amber-50/50 dark:bg-amber-950/30">
                                    <span className="text-[10px] text-amber-800 font-semibold">Qty:</span>
                                    <input
                                      type="number"
                                      min="1"
                                      value={u}
                                      onChange={(e) => {
                                        const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                                        setCustomItems(prev => prev.map(ci => ci.id === item.id ? { ...ci, unit: val } : ci));
                                      }}
                                      className="w-8 text-center text-xs font-bold bg-transparent focus:outline-none"
                                    />
                                  </div>
                                  <span className="font-semibold text-amber-900 dark:text-amber-400">${lineTot.toFixed(2)}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="w-6 h-6 text-slate-400 hover:text-destructive"
                                    onClick={() => setCustomItems((prev) => prev.filter((i) => i.id !== item.id))}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 pt-1 border-t border-amber-200/50">
                        <Input
                          type="text"
                          value={customName}
                          onChange={(e) => setCustomName(e.target.value)}
                          placeholder="Item Name (e.g. Bandage)"
                          className="sm:col-span-3 h-8 text-xs bg-white dark:bg-slate-900"
                        />
                        <Input
                          type="text"
                          value={customDesc}
                          onChange={(e) => setCustomDesc(e.target.value)}
                          placeholder="Notes (optional)"
                          className="sm:col-span-3 h-8 text-xs bg-white dark:bg-slate-900"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          value={customPrice}
                          onChange={(e) => setCustomPrice(e.target.value)}
                          placeholder="Price ($)"
                          className="sm:col-span-2 h-8 text-xs bg-white dark:bg-slate-900"
                        />
                        <Input
                          type="number"
                          min="1"
                          value={customUnit}
                          onChange={(e) => setCustomUnit(e.target.value)}
                          placeholder="Qty"
                          className="sm:col-span-2 h-8 text-xs bg-white dark:bg-slate-900"
                          title="Quantity / Units"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            if (!customName.trim()) return toast({ title: "Validation error", description: "Please enter custom item name", variant: "destructive" });
                            const p = parseFloat(customPrice);
                            if (isNaN(p) || p <= 0) return toast({ title: "Validation error", description: "Please enter a valid price", variant: "destructive" });
                            const u = Math.max(1, parseInt(customUnit, 10) || 1);
                            setCustomItems((prev) => [
                              ...prev,
                              {
                                id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                                name: customName.trim(),
                                description: customDesc.trim() || undefined,
                                price: p,
                                unit: u
                              },
                            ]);
                            setCustomName("");
                            setCustomDesc("");
                            setCustomPrice("");
                            setCustomUnit("1");
                          }}
                          className="sm:col-span-2 h-8 text-xs bg-amber-600 hover:bg-amber-500 text-white gap-1"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add
                        </Button>
                      </div>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700/50 rounded-xl p-4 mt-2">
                      <div className="flex items-center justify-between mb-4">
                        <Label className="text-muted-foreground text-sm">Billable Amount</Label>
                        <span className="font-semibold text-lg text-primary">${calculatedTotal.toFixed(2)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700/50">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1.5 block">Adjusted (%)</Label>
                          <Input
                            type="number"
                            value={form.discountPercent}
                            onChange={e => {
                              const val = e.target.value;
                              setForm({
                                ...form,
                                discountPercent: val,
                                finalPrice: val === "" || !calculatedTotal ? "" : (calculatedTotal * (1 - Number(val) / 100)).toFixed(2)
                              });
                            }}
                            min="0"
                            max="100"
                            className="rounded-xl bg-white dark:bg-slate-950"
                            data-testid="input-discount"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1.5 block">Adjusted Price ($)</Label>
                          <Input
                            type="number"
                            value={form.finalPrice}
                            onChange={e => {
                              const val = e.target.value;
                              setForm({
                                ...form,
                                finalPrice: val,
                                discountPercent: val === "" || !calculatedTotal ? "" : (((calculatedTotal - Number(val)) / calculatedTotal) * 100).toFixed(2)
                              });
                            }}
                            className="rounded-xl bg-white dark:bg-slate-950 font-medium"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Structure & Recurring Billing */}
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-bold flex items-center gap-2 text-foreground">
                          <CreditCard className="w-4 h-4 text-primary" /> Payment Model & Subscription Structure
                        </Label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Configure whether this package is billed as a one-time charge or converted into a recurring payment plan.
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <label htmlFor="modal-subscription-toggle" className="text-xs font-semibold cursor-pointer text-foreground flex items-center gap-1.5">
                            Enable Recurring Payment Schedule / Subscription
                          </label>
                          <p className="text-[11px] text-muted-foreground">
                            Allows members to subscribe to this bundle with scheduled installment payments.
                          </p>
                        </div>
                        <Switch
                          id="modal-subscription-toggle"
                          checked={isSubscription}
                          onCheckedChange={checked => {
                            setIsSubscription(checked);
                            const fp = Number(form.finalPrice) || calculatedTotal || 0;
                            if (checked && !recurringAmount && fp > 0) {
                              setRecurringAmount((fp / 12).toFixed(2));
                            }
                          }}
                        />
                      </div>

                      {isSubscription && (
                        <div className="space-y-3 pt-2 border-t border-border/60">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs font-semibold">Billing Schedule / Interval</Label>
                              <Select
                                value={subscriptionInterval}
                                onValueChange={val => {
                                  setSubscriptionInterval(val);
                                  const fp = Number(form.finalPrice) || calculatedTotal || 0;
                                  if (fp > 0) {
                                    let divisor = 12;
                                    if (val === "QUARTERLY") divisor = 4;
                                    if (val === "BI_WEEKLY") divisor = 26;
                                    if (val === "WEEKLY") divisor = 52;
                                    if (val === "YEARLY") divisor = 1;
                                    setRecurringAmount((fp / divisor).toFixed(2));
                                  }
                                }}
                              >
                                <SelectTrigger className="mt-1 rounded-lg h-9 text-xs bg-background">
                                  <SelectValue placeholder="Select Frequency" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl text-xs">
                                  <SelectItem value="MONTHLY">Monthly Billing</SelectItem>
                                  <SelectItem value="QUARTERLY">Quarterly Billing</SelectItem>
                                  <SelectItem value="BI_WEEKLY">Bi-Weekly Billing</SelectItem>
                                  <SelectItem value="WEEKLY">Weekly Billing</SelectItem>
                                  <SelectItem value="YEARLY">Annual Billing</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label className="text-xs font-semibold flex items-center justify-between">
                                <span>Recurring Installment ($)</span>
                              </Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={recurringAmount}
                                onChange={e => setRecurringAmount(e.target.value)}
                                className="mt-1 rounded-lg h-9 text-xs bg-background font-bold"
                              />
                            </div>

                            <div>
                              <Label className="text-xs font-semibold">Total Billing Cycles</Label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="e.g. 12 (Blank = Continuous)"
                                value={billingCycles}
                                onChange={e => setBillingCycles(e.target.value)}
                                className="mt-1 rounded-lg h-9 text-xs bg-background"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {isGroup && bundleScope === 'GROUP' && (
                      <div className="mt-4 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <Label className="text-sm font-semibold block mb-3">Participating Providers</Label>
                        <div className="max-h-40 overflow-y-auto space-y-2 pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                          {myLinkedProviders.map((p: any) => {
                            const provId = p.providerId || p.id;
                            if (!provId) return null;
                            return (
                              <div key={provId} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`provider-${provId}`}
                                  checked={true}
                                  disabled
                                />
                                <Label htmlFor={`provider-${provId}`} className="text-sm font-medium opacity-80 cursor-default">
                                  {p.name}
                                </Label>
                              </div>
                            );
                          })}
                          {myLinkedProviders.length === 0 && <p className="text-sm text-muted-foreground">No linked providers found.</p>}
                        </div>
                      </div>
                    )}


                  </div>

                  {/* Disclaimer Checkbox before bundle creation (only shown when creating a new bundle) */}
                  {!editId && (
                    <div className="flex items-start space-x-2.5 p-3.5 rounded-xl border border-amber-200/80 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800/40 text-amber-900 dark:text-amber-200 mb-4">
                      <Checkbox
                        id="disclaimer-checkbox"
                        checked={disclaimerAccepted}
                        onCheckedChange={(checked) => setDisclaimerAccepted(!!checked)}
                        className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                      />
                      <Label htmlFor="disclaimer-checkbox" className="text-xs font-medium leading-relaxed cursor-pointer select-none">
                        <span className="font-bold">Disclaimer:</span> I acknowledge and verify that the services, billing codes, and adjusted pricing configured in this bundle comply with all practice policies and regulatory guidelines.
                      </Label>
                    </div>
                  )}

                  <Button onClick={handleSubmit} disabled={!form.name || (!editId && !disclaimerAccepted) || createBundle.isPending || updateBundle.isPending} className="w-full rounded-xl bg-gradient-to-r from-primary to-secondary text-white border-0" data-testid="button-create-bundle">
                    {createBundle.isPending || updateBundle.isPending ? "Saving..." : (editId ? "Update Bundle" : "Create Bundle")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-6 border-b border-border mb-6 overflow-x-auto pb-0.5 w-full max-w-full">
          {/* 1. Active Tab */}
          <button
            type="button"
            onClick={() => {
              setActiveTab("active");
              setCurrentPage(1);
            }}
            className={`relative pb-3 pt-1 text-sm font-semibold transition-colors flex items-center gap-2 bg-transparent border-0 cursor-pointer whitespace-nowrap ${
              activeTab === "active"
                ? "text-foreground font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-active-bundles"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span>Active</span>
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/60">
                {activeCount}
              </span>
            )}
            {activeTab === "active" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>

          {/* 2. Inactive Tab */}
          <button
            type="button"
            onClick={() => {
              setActiveTab("inactive");
              setCurrentPage(1);
            }}
            className={`relative pb-3 pt-1 text-sm font-semibold transition-colors flex items-center gap-2 bg-transparent border-0 cursor-pointer whitespace-nowrap ${
              activeTab === "inactive"
                ? "text-foreground font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-inactive-bundles"
          >
            <XCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span>Inactive</span>
            {inactiveCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/60">
                {inactiveCount}
              </span>
            )}
            {activeTab === "inactive" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>

          {/* 3. Archived Tab */}
          <button
            type="button"
            onClick={() => {
              setActiveTab("archived");
              setCurrentPage(1);
            }}
            className={`relative pb-3 pt-1 text-sm font-semibold transition-colors flex items-center gap-2 bg-transparent border-0 cursor-pointer whitespace-nowrap ${
              activeTab === "archived"
                ? "text-foreground font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-archived-bundles"
          >
            <Archive className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span>Archived</span>
            {archivedCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-200/60 dark:border-purple-800/60">
                {archivedCount}
              </span>
            )}
            {activeTab === "archived" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>

          {/* 4. Deleted Tab */}
          <button
            type="button"
            onClick={() => {
              setActiveTab("deleted");
              setCurrentPage(1);
            }}
            className={`relative pb-3 pt-1 text-sm font-semibold transition-colors flex items-center gap-2 bg-transparent border-0 cursor-pointer whitespace-nowrap ${
              activeTab === "deleted"
                ? "text-foreground font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-deleted-bundles"
          >
            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
            <span>Deleted</span>
            {deletedCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-red-50 text-red-700 dark:bg-red-950/60 dark:text-red-300 border border-red-200/60 dark:border-red-800/60">
                {deletedCount}
              </span>
            )}
            {activeTab === "deleted" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        </div>

        {/* Tag Cloud Filter Bar */}
        {existingTags.length > 0 && (
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 w-full max-w-full">
            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1 flex-shrink-0">
              <Tag className="w-3.5 h-3.5" /> Filter by Tag:
            </span>
            <button
              type="button"
              onClick={() => setSelectedTagFilter(null)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                selectedTagFilter === null
                  ? "bg-primary text-primary-foreground shadow-2xs font-bold"
                  : "bg-muted text-muted-foreground hover:bg-muted/80 border border-border"
              }`}
            >
              All Tags
            </button>
            {existingTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => setSelectedTagFilter(selectedTagFilter === tag ? null : tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  selectedTagFilter === tag
                    ? "bg-primary text-primary-foreground shadow-2xs font-bold"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted border border-border"
                }`}
              >
                #{tag}
                {selectedTagFilter === tag && <X className="w-3 h-3 ml-0.5" />}
              </button>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-card rounded-2xl border border-border p-5"><Skeleton className="h-24 w-full" /></div>
            ))}
          </div>
        ) : (bundles?.length ?? 0) === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Package className="w-14 h-14 mx-auto mb-4 opacity-30" />
            <p className="font-medium">No bundles yet</p>
            <p className="text-sm">Create service bundles to simplify billing and offer discounts.</p>
          </div>
        ) : viewMode === "list" ? (
          /* ── List / Table View Format ── */
          <div className="bg-white dark:bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[850px]">
                <thead>
                  <tr className="bg-muted/40 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    <th className="py-3.5 px-4 sm:px-6 min-w-[280px]">Bundle Details</th>
                    <th className="py-3.5 px-4 sm:px-6 min-w-[150px]">Included Services</th>
                    <th className="py-3.5 px-4 sm:px-6 min-w-[130px]">Price Summary</th>
                    <th className="py-3.5 px-4 sm:px-6 text-center w-28">Status</th>
                    <th className="py-3.5 px-4 sm:px-6 text-right min-w-[260px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-sm">
                  {sortedBundles.map((bundle, index) => {
                    const isProviderSpecific = bundle.type?.startsWith('PROVIDER_');
                    let providerMedicareRate = parseFloat(user?.provider?.medicareCharge || "100") || 100;

                    if (isProviderSpecific) {
                      const pId = bundle.type!.split('_')[1];
                      const provider = myLinkedProviders.find((p: any) => String(p.providerId || p.id) === String(pId));
                      if (provider && provider.medicareCharge) {
                        providerMedicareRate = parseFloat(String(provider.medicareCharge).replace('%', '')) || 100;
                      }
                    }

                    const displayCptCodes = bundle.cptCodes?.map(c => {
                      const master = rawMasterCodes.find((m: any) => m.code === c.code);
                      if (master && isGroup && isProviderSpecific) {
                        return { ...c, displayPrice: (master.standardPrice + master.standardPrice * (providerMedicareRate / 100)).toFixed(2) };
                      }
                      return { ...c, displayPrice: Number(c.price).toFixed(2) };
                    }) || [];

                    const categoryName = typeof bundle.category === 'object' && bundle.category !== null
                      ? ((bundle.category as any)?.name || (bundle.category as any)?.code || "")
                      : String(bundle.category || "");

                    return (
                      <tr
                        key={bundle.id}
                        onClick={() => handleEdit(bundle)}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer"
                        data-testid={`bundle-row-${bundle.id}`}
                      >
                        {/* 1. Bundle Details */}
                        <td className="py-4 px-4 sm:px-6 align-middle min-w-[280px]">
                          <div className="flex items-center gap-3.5">
                            {bundle.thumbnailUrl ? (
                              <img src={getImageUrl(bundle.thumbnailUrl)} alt={bundle.name} className="w-11 h-11 rounded-xl object-cover border border-border shadow-2xs shrink-0" />
                            ) : (
                              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center border border-primary/20 shadow-inner shrink-0">
                                <Package className="w-5 h-5 text-primary" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3
                                  onClick={(e) => { e.stopPropagation(); setViewBundle(bundle); }}
                                  className="font-bold text-foreground hover:text-primary cursor-pointer transition-colors text-sm sm:text-base leading-snug break-words"
                                >
                                  {bundle.name}
                                </h3>
                                {bundle.isFeatured && (
                                  <span className="inline-flex items-center gap-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide shrink-0">
                                    <Star className="w-3 h-3 fill-amber-400 text-amber-500" /> Featured
                                  </span>
                                )}
                              </div>
                              {bundle.description && (
                                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5 break-words">{bundle.description}</p>
                              )}
                              {Array.isArray((bundle as any).tags) && (bundle as any).tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {(bundle as any).tags.map((t: string) => (
                                    <span key={t} className="text-[10px] bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded-full font-medium">
                                      #{t}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* 2. Included Services */}
                        <td className="py-4 px-4 sm:px-6 align-middle min-w-[150px]">
                          <div className="flex items-center gap-1.5 flex-wrap max-w-xs">
                            {displayCptCodes && displayCptCodes.length > 0 ? (
                              displayCptCodes.map((c: any) => (
                                <Badge key={c.id || c.code} variant="outline" className="font-mono text-xs bg-primary/5 text-primary border-primary/20 px-2.5 py-0.5 font-bold rounded-lg shadow-2xs">
                                  {c.code}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground italic">No services</span>
                            )}
                          </div>
                        </td>

                        {/* 4. Total Value & Price */}
                        <td className="py-4 px-4 sm:px-6 align-middle min-w-[130px] whitespace-nowrap">
                          <div>
                            <div className="text-xs text-muted-foreground line-through">${bundle.totalPrice.toFixed(2)}</div>
                            <div className="text-base sm:text-lg font-black text-primary">${bundle.finalPrice.toFixed(2)}</div>
                            {bundle.isSubscription && (
                              <div className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-md mt-1">
                                <RefreshCw className="w-2.5 h-2.5" />
                                ${(bundle.recurringAmount || (bundle.finalPrice / 12)).toFixed(2)} / {(bundle.subscriptionInterval || 'MONTHLY').toLowerCase().replace('_', '-')}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* 5. Status */}
                        <td className="py-4 px-4 sm:px-6 align-middle text-center whitespace-nowrap w-28">
                          <Badge className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${bundle.status === "active" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 shadow-2xs hover:bg-emerald-500/20" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-200"}`}>
                            {bundle.status}
                          </Badge>
                        </td>

                        {/* 6. Actions */}
                        <td className="py-4 px-4 sm:px-6 align-middle text-right whitespace-nowrap min-w-[260px]">
                          <div className="flex items-center justify-end gap-1.5">
                            {sortBy === "custom" && (
                              <div className="inline-flex items-center rounded-lg border border-border/80 bg-muted/40 p-0.5 shadow-2xs mr-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={index === 0 || reorderBundles.isPending}
                                  className="w-7 h-7 rounded-md hover:bg-background text-muted-foreground hover:text-foreground disabled:opacity-25"
                                  onClick={(e) => { e.stopPropagation(); handleMove(index, "up"); }}
                                  title="Move Up"
                                >
                                  <ArrowUp className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={index === sortedBundles.length - 1 || reorderBundles.isPending}
                                  className="w-7 h-7 rounded-md hover:bg-background text-muted-foreground hover:text-foreground disabled:opacity-25"
                                  onClick={(e) => { e.stopPropagation(); handleMove(index, "down"); }}
                                  title="Move Down"
                                >
                                  <ArrowDown className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 rounded-lg border border-border/60 bg-background/60 hover:bg-primary/10 text-muted-foreground hover:text-primary shadow-2xs transition-all"
                              onClick={(e) => { e.stopPropagation(); setViewBundle(bundle); }}
                              title="View Details"
                              data-testid={`button-view-bundle-${bundle.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <a href={`/bundle/${bundle.id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 h-8 rounded-lg border border-border/60 bg-background/60 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 shadow-2xs transition-all"
                                title="Open Standalone Product Page"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </a>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 rounded-lg border border-border/60 bg-background/60 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 shadow-2xs transition-all"
                              onClick={(e) => { e.stopPropagation(); handleDuplicate(bundle); }}
                              title="Duplicate / Copy Bundle"
                              data-testid={`button-duplicate-bundle-${bundle.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            {(bundle.status === "inactive" || bundle.status === "archived" || bundle.status === "deleted") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 h-8 rounded-lg border border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 shadow-2xs transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateBundle.mutate({ id: bundle.id, data: { status: "active" } }, {
                                    onSuccess: () => {
                                      queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
                                      toast({ title: "Bundle Restored", description: `"${bundle.name}" is now Active.` });
                                    }
                                  });
                                }}
                                title="Restore / Reactivate Bundle"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 rounded-lg border border-border/60 bg-background/60 hover:bg-primary/10 text-muted-foreground hover:text-primary shadow-2xs transition-all"
                              onClick={(e) => { e.stopPropagation(); setLocation(`/provider/bundles/edit/${bundle.id}`); }}
                              title="Full Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 rounded-lg border border-border/60 bg-background/60 hover:bg-muted text-muted-foreground hover:text-foreground shadow-2xs transition-all"
                              onClick={(e) => { e.stopPropagation(); handleEdit(bundle); }}
                              title="Quick Edit Pop-up"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 rounded-lg border border-border/60 bg-background/60 hover:border-destructive/30 hover:bg-destructive/10 text-muted-foreground hover:text-destructive shadow-2xs transition-all"
                              onClick={(e) => { e.stopPropagation(); handleDeleteClick(bundle); }}
                              title="Delete Bundle"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* ── Grid View Format ── */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {sortedBundles.map((bundle, i) => {
              const isProviderSpecific = bundle.type?.startsWith('PROVIDER_');
              let providerMedicareRate = parseFloat(user?.provider?.medicareCharge || "100") || 100;

              if (isProviderSpecific) {
                const pId = bundle.type!.split('_')[1];
                const provider = myLinkedProviders.find((p: any) => String(p.providerId || p.id) === String(pId));
                if (provider && provider.medicareCharge) {
                  providerMedicareRate = parseFloat(String(provider.medicareCharge).replace('%', '')) || 100;
                }
              }

              const displayCptCodes = bundle.cptCodes?.map(c => {
                const master = rawMasterCodes.find((m: any) => m.code === c.code);
                if (master && isGroup && isProviderSpecific) {
                  return { ...c, displayPrice: (master.standardPrice + master.standardPrice * (providerMedicareRate / 100)).toFixed(2) };
                }
                return { ...c, displayPrice: Number(c.price).toFixed(2) };
              }) || [];

              return (
                <motion.div
                  key={bundle.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, ease: "easeOut" }}
                  whileHover={{ y: -4, scale: 1.01 }}
                  onClick={() => handleEdit(bundle)}
                  className="group relative bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200/60 dark:border-slate-800/60 p-5 sm:p-6 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
                  data-testid={`bundle-${bundle.id}`}
                >
                  {/* Decorative background blur */}
                  <div className="absolute -right-12 -top-12 w-48 h-48 bg-gradient-to-br from-primary/10 to-secondary/10 dark:from-primary/5 dark:to-secondary/5 rounded-full blur-3xl group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-500" />

                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-start justify-between gap-2 mb-4">
                      {bundle.thumbnailUrl ? (
                        <img src={getImageUrl(bundle.thumbnailUrl)} alt={bundle.name} className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl object-cover border border-primary/10 dark:border-primary/20 shadow-inner flex-shrink-0" />
                      ) : (
                        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 dark:from-primary/20 dark:to-secondary/20 flex items-center justify-center shadow-inner border border-primary/10 dark:border-primary/20 flex-shrink-0">
                          <Package className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                        </div>
                      )}
                      <div className="flex flex-wrap justify-end gap-1 bg-slate-50 dark:bg-slate-800/50 p-1 rounded-xl border border-slate-100 dark:border-slate-700/50 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity max-w-[calc(100%-3.5rem)]">
                        {sortBy === "custom" && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={i === 0 || reorderBundles.isPending}
                              className="w-8 h-8 rounded-lg hover:bg-white dark:hover:bg-slate-700 shadow-sm text-slate-500 disabled:opacity-30"
                              onClick={(e) => { e.stopPropagation(); handleMove(i, "up"); }}
                              title="Move Up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={i === sortedBundles.length - 1 || reorderBundles.isPending}
                              className="w-8 h-8 rounded-lg hover:bg-white dark:hover:bg-slate-700 shadow-sm text-slate-500 disabled:opacity-30"
                              onClick={(e) => { e.stopPropagation(); handleMove(i, "down"); }}
                              title="Move Down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg hover:bg-white dark:hover:bg-slate-700 shadow-sm text-primary" onClick={(e) => { e.stopPropagation(); setViewBundle(bundle); }} title="View Details" data-testid={`button-view-bundle-${bundle.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <a href={`/bundle/${bundle.id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg hover:bg-white dark:hover:bg-slate-700 shadow-sm text-indigo-600 dark:text-indigo-400" title="Open Standalone Product Page">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                        {(bundle.status === "inactive" || bundle.status === "archived" || bundle.status === "deleted") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-8 h-8 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 shadow-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateBundle.mutate({ id: bundle.id, data: { status: "active" } }, {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
                                  toast({ title: "Bundle Restored", description: `"${bundle.name}" is now Active.` });
                                }
                              });
                            }}
                            title="Restore / Reactivate Bundle"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg hover:bg-white dark:hover:bg-slate-700 shadow-sm text-indigo-600 dark:text-indigo-400" onClick={(e) => { e.stopPropagation(); handleDuplicate(bundle); }} title="Duplicate / Copy Bundle" data-testid={`button-duplicate-bundle-${bundle.id}`}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg hover:bg-white dark:hover:bg-slate-700 shadow-sm" onClick={(e) => { e.stopPropagation(); handleEdit(bundle); }} title="Edit Bundle" data-testid={`button-edit-bundle-${bundle.id}`}>
                          <Edit2 className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-red-50 dark:hover:bg-red-900/20 shadow-sm" onClick={(e) => { e.stopPropagation(); handleDeleteClick(bundle); }} title="Delete Bundle" data-testid={`button-delete-bundle-${bundle.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <h3 className="text-lg sm:text-xl font-bold mb-1.5 text-slate-900 dark:text-white leading-tight tracking-tight break-words">{bundle.name}</h3>
                    {bundle.description && <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 leading-relaxed line-clamp-2 break-words">{bundle.description}</p>}

                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 font-semibold gap-1.5 px-2.5 py-0.5 shrink-0">
                        <Tag className="w-3.5 h-3.5" />{bundle.discountPercent}% off
                      </Badge>
                    </div>

                    {Array.isArray((bundle as any).tags) && (bundle as any).tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {(bundle as any).tags.map((t: string) => (
                          <span key={t} className="text-xs bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded-full font-medium">
                            #{t}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mb-5 space-y-2 flex-1">
                      <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Included Services</div>
                      {displayCptCodes && displayCptCodes.length > 0 ? (
                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                          {displayCptCodes.map((c: any) => (
                            <div key={c.id} className="flex items-center justify-between text-sm py-1 gap-2">
                              <div className="flex items-center gap-2 min-w-0 pr-2">
                                <span className="font-mono text-xs px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-bold shrink-0">{c.code}</span>
                                <span className="text-slate-600 dark:text-slate-300 truncate text-xs sm:text-sm">{c.description}</span>
                              </div>
                              <span className="font-medium text-slate-700 dark:text-slate-200 shrink-0 text-xs sm:text-sm">${c.displayPrice}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400 italic">No services added</div>
                      )}
                      {(bundle as any).customItems && Array.isArray((bundle as any).customItems) && (bundle as any).customItems.length > 0 && (
                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1">
                          <div className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Custom Items</div>
                          {(bundle as any).customItems.map((ci: any) => (
                            <div key={ci.id || ci.name} className="flex items-center justify-between text-xs py-0.5 text-amber-900 dark:text-amber-300 gap-2">
                              <span className="truncate min-w-0">{ci.name}</span>
                              <span className="font-semibold shrink-0">${Number(ci.price).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 mt-auto border-t border-slate-100 dark:border-slate-800">
                      <div className="flex items-end justify-between gap-2 flex-wrap sm:flex-nowrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-medium text-slate-400 shrink-0">Total Value</span>
                            <span className="text-sm text-slate-400 line-through decoration-slate-300 dark:decoration-slate-600 truncate">${bundle.totalPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex items-baseline gap-1 text-primary">
                            <span className="text-2xl sm:text-3xl font-black tracking-tight">${bundle.finalPrice.toFixed(2)}</span>
                          </div>
                        </div>
                        <Badge className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 mb-1 ${bundle.status === "active" ? "bg-emerald-500 text-white shadow-sm shadow-emerald-500/20" : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}>{bundle.status}</Badge>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {!isLoading && totalPages > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-auto pt-6 text-sm text-muted-foreground border-t border-border flex-shrink-0">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-4 text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-border rounded-md bg-transparent px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary text-xs sm:text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <span>
                Showing {totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalEntries)} of {totalEntries}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex gap-1 overflow-x-auto max-w-[200px] sm:max-w-none py-1">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 rounded-md flex items-center justify-center font-medium shrink-0 ${currentPage === i + 1
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <ConfirmDialog
          open={!!deleteId}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteId(null);
              setDeleteName("");
            }
          }}
          title={activeTab === "deleted" ? "Permanently Delete Bundle?" : "Delete Bundle?"}
          description={
            activeTab === "deleted"
              ? `Are you sure you want to PERMANENTLY delete "${deleteName}"? This will completely remove it from the database and cannot be undone.`
              : `Are you sure you want to delete "${deleteName}"? It will be moved to the Deleted tab.`
          }
          confirmText={activeTab === "deleted" ? "Permanently Delete" : "Delete Bundle"}
          variant="destructive"
          onConfirm={handleDelete}
          isConfirming={deleteBundle.isPending}
        />

        {/* ── View Bundle Details Dialog ── */}
        <Dialog open={!!viewBundle} onOpenChange={(open) => { if (!open) setViewBundle(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8">
            {viewBundle && (() => {
              const isProviderSpecific = viewBundle.type?.startsWith('PROVIDER_');
              let providerMedicareRate = parseFloat(user?.provider?.medicareCharge || "100") || 100;

              if (isProviderSpecific) {
                const pId = viewBundle.type!.split('_')[1];
                const provider = myLinkedProviders.find((p: any) => String(p.providerId || p.id) === String(pId));
                if (provider && provider.medicareCharge) {
                  providerMedicareRate = parseFloat(String(provider.medicareCharge).replace('%', '')) || 100;
                }
              }

              const displayCptCodes = viewBundle.cptCodes?.map(c => {
                const master = rawMasterCodes.find((m: any) => m.code === c.code);
                if (master && isGroup && isProviderSpecific) {
                  return { ...c, displayPrice: (master.standardPrice + master.standardPrice * (providerMedicareRate / 100)).toFixed(2) };
                }
                return { ...c, displayPrice: Number(c.price).toFixed(2) };
              }) || [];

              const savings = viewBundle.totalPrice - viewBundle.finalPrice;

              return (
                <div className="space-y-6">
                  <DialogHeader className="pb-4 border-b border-border">
                    <div className="flex items-start gap-4">
                      {viewBundle.thumbnailUrl ? (
                        <img src={getImageUrl(viewBundle.thumbnailUrl)} alt={viewBundle.name} className="w-14 h-14 rounded-2xl object-cover border border-primary/20 shadow-inner flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center border border-primary/20 shadow-inner flex-shrink-0">
                          <Package className="w-7 h-7 text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <DialogTitle className="text-2xl font-bold tracking-tight">{viewBundle.name}</DialogTitle>
                          <Badge className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${viewBundle.status === "active" ? "bg-emerald-500 text-white shadow-xs" : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}>
                            {viewBundle.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 font-semibold gap-1">
                            <Tag className="w-3.5 h-3.5" />{viewBundle.discountPercent}% OFF
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </DialogHeader>

                  {viewBundle.description && (
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Description</h4>
                      <p className="text-sm text-foreground bg-muted/40 p-3.5 rounded-xl border border-border/50 leading-relaxed">{viewBundle.description}</p>
                    </div>
                  )}

                  {/* All Included Services List */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        All Included CPT Codes & Services ({displayCptCodes.length})
                      </h4>
                      <span className="text-xs text-muted-foreground font-medium">Total Value: ${viewBundle.totalPrice.toFixed(2)}</span>
                    </div>

                    <div className="border border-border rounded-2xl overflow-hidden bg-card shadow-xs">
                      <div className="max-h-72 overflow-y-auto divide-y divide-border">
                        {displayCptCodes.length > 0 ? (
                          displayCptCodes.map((c: any, index: number) => (
                            <div key={c.id || index} className="p-3.5 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors">
                              <div className="flex items-center gap-3 min-w-0">
                                <span className="font-mono text-xs px-2 py-1 rounded-lg bg-primary/10 text-primary font-bold flex-shrink-0">
                                  {c.code}
                                </span>
                                <span className="text-sm font-medium text-foreground truncate">
                                  {c.description}
                                </span>
                              </div>
                              <span className="font-bold text-sm text-foreground flex-shrink-0">
                                ${c.displayPrice}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="p-4 text-center text-sm text-muted-foreground">No CPT codes in this bundle</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Custom Items if any */}
                  {(viewBundle as any).customItems && Array.isArray((viewBundle as any).customItems) && (viewBundle as any).customItems.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                        Custom Items ({(viewBundle as any).customItems.length})
                      </h4>
                      <div className="border border-amber-200 dark:border-amber-900/50 rounded-2xl overflow-hidden bg-amber-50/50 dark:bg-amber-950/20 divide-y divide-amber-100 dark:divide-amber-900/30">
                        {(viewBundle as any).customItems.map((ci: any, idx: number) => (
                          <div key={ci.id || idx} className="p-3 flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-amber-950 dark:text-amber-200">{ci.name}</p>
                              {ci.description && <p className="text-xs text-amber-800/80 dark:text-amber-300/80">{ci.description}</p>}
                            </div>
                            <span className="font-bold text-sm text-amber-900 dark:text-amber-200">${Number(ci.price).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Financial Summary Card */}
                  <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 dark:from-slate-900 dark:to-emerald-950/20 border border-emerald-500/20 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="space-y-1 text-center sm:text-left">
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Billable Amount</p>
                      <p className="text-lg font-semibold text-slate-400 line-through">${viewBundle.totalPrice.toFixed(2)}</p>
                    </div>

                    {savings > 0 && (
                      <div className="text-center sm:text-left">
                        <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Bundle Savings</p>
                        <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">-${savings.toFixed(2)} ({viewBundle.discountPercent}%)</p>
                      </div>
                    )}

                    <div className="text-center sm:text-right">
                      <p className="text-xs font-semibold text-primary uppercase tracking-wider">Adjusted Price</p>
                      <p className="text-3xl font-black text-primary tracking-tight">${viewBundle.finalPrice.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => setViewBundle(null)} className="rounded-xl">
                      Close
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const b = viewBundle;
                        setViewBundle(null);
                        handleDuplicate(b);
                      }}
                      className="rounded-xl gap-2 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-900 hover:bg-indigo-50"
                    >
                      <Copy className="w-4 h-4" /> Duplicate
                    </Button>
                    <Button
                      onClick={() => {
                        const b = viewBundle;
                        setViewBundle(null);
                        handleEdit(b);
                      }}
                      className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-2"
                    >
                      <Edit2 className="w-4 h-4" /> Edit Bundle
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={!!archiveConfirmBundle}
          onOpenChange={(val) => { if (!val) setArchiveConfirmBundle(null); }}
          title="Archive Service Bundle"
          description={`"${archiveConfirmBundle?.name}" is currently linked to 1 or more patient appointment records. To preserve historical patient invoices and reports, it cannot be deleted. Would you like to Archive this bundle instead?`}
          confirmText="Archive Bundle"
          cancelText="Cancel"
          onConfirm={() => {
            if (archiveConfirmBundle) {
              updateBundle.mutate({
                id: archiveConfirmBundle.id,
                data: { status: "archived" }
              }, {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
                  toast({ title: "Bundle archived", description: `${archiveConfirmBundle.name} has been archived.` });
                  setArchiveConfirmBundle(null);
                },
                onError: (err: any) => {
                  toast({ title: "Archive failed", description: err.message, variant: "destructive" });
                }
              });
            }
          }}
        />
      </div>
    </ProviderLayout>
  );
}
